import React, { useState, useEffect, useCallback, useMemo } from 'react';
import referenceLabService from '../../services/referenceLabService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import './InvestigationMasterPage.css';

const INITIAL_LAB = {
  referenceLabId: 0,
  labCode: '',
  labName: '',
  address: '',
  contactPerson: '',
  contactPhone: '',
  email: '',
  accreditation: 'NABL ISO 15189',
  agreementStartDate: '',
  agreementEndDate: '',
  defaultTatHours: 24,
  billingTerms: 'Monthly B2B Net 30',
  isActive: true
};

export const ReferenceLabMasterPage = ({ showToast }) => {
  const [viewMode, setViewMode] = useState('list');
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState(INITIAL_LAB);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const fetchLabs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await referenceLabService.getReferenceLabs({
        search: searchQuery.trim() || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active'
      });
      if (res.success && res.data) {
        setItems(res.data || []);
      }
    } catch (err) {
      showToast?.('Failed to load reference laboratories.', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, showToast]);

  useEffect(() => {
    fetchLabs();
  }, [fetchLabs]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAddNew = () => {
    setFormData(INITIAL_LAB);
    setIsEditMode(false);
    setErrors({});
    setViewMode('form');
  };

  const handleEdit = (lab) => {
    setFormData({
      referenceLabId: lab.referenceLabId,
      labCode: lab.labCode || '',
      labName: lab.labName || '',
      address: lab.address || '',
      contactPerson: lab.contactPerson || '',
      contactPhone: lab.contactPhone || '',
      email: lab.email || '',
      accreditation: lab.accreditation || 'NABL ISO 15189',
      agreementStartDate: lab.agreementStartDate ? lab.agreementStartDate.substring(0, 10) : '',
      agreementEndDate: lab.agreementEndDate ? lab.agreementEndDate.substring(0, 10) : '',
      defaultTatHours: lab.defaultTatHours || 24,
      billingTerms: lab.billingTerms || 'Monthly B2B Net 30',
      isActive: lab.isActive
    });
    setIsEditMode(true);
    setErrors({});
    setViewMode('form');
  };

  const validate = () => {
    const errs = {};
    if (!formData.labCode.trim()) errs.labCode = 'Lab Code is mandatory.';
    if (!formData.labName.trim()) errs.labName = 'Lab Name is mandatory.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) {
      showToast?.('Please resolve validation errors before saving.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        labCode: formData.labCode.trim(),
        labName: formData.labName.trim(),
        address: formData.address.trim() || null,
        contactPerson: formData.contactPerson.trim() || null,
        contactPhone: formData.contactPhone.trim() || null,
        email: formData.email.trim() || null,
        accreditation: formData.accreditation.trim() || null,
        agreementStartDate: formData.agreementStartDate || null,
        agreementEndDate: formData.agreementEndDate || null,
        defaultTatHours: parseInt(formData.defaultTatHours, 10) || 24,
        billingTerms: formData.billingTerms.trim() || null,
        isActive: formData.isActive
      };

      if (isEditMode) {
        await referenceLabService.updateReferenceLab(formData.referenceLabId, payload);
        showToast?.('Reference laboratory updated successfully.', 'success');
      } else {
        await referenceLabService.createReferenceLab(payload);
        showToast?.('Reference laboratory created successfully.', 'success');
      }

      await fetchLabs();
      setViewMode('list');
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Failed to save reference laboratory.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = (lab) => {
    setConfirmTarget({
      id: lab.referenceLabId,
      name: lab.labName,
      action: lab.isActive ? 'deactivate' : 'activate'
    });
  };

  const executeStatusChange = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const newStatus = confirmTarget.action === 'activate';
      await referenceLabService.updateStatus(confirmTarget.id, newStatus);
      showToast?.(`Reference laboratory successfully ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
      setItems(prev => prev.map(item => item.referenceLabId === confirmTarget.id ? { ...item, isActive: newStatus } : item));
      setConfirmTarget(null);
    } catch (err) {
      showToast?.('Failed to update status.', 'error');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Filtered & Paginated items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = !searchQuery.trim() || 
        (item.labName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.labCode || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && item.isActive) ||
        (statusFilter === 'inactive' && !item.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [items, searchQuery, statusFilter]);

  const {
    page,
    pageSize,
    totalRecords,
    setPage,
    setPageSize,
    paginatedItems: paginatedData,
  } = usePagination({
    items: filteredItems,
    initialPageSize: 25,
    resetDeps: [searchQuery, statusFilter],
  });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? 'Update Reference Laboratory' : 'Register Reference Laboratory') : 'Reference Laboratory Master'}
      masterName="Reference Laboratory"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form' && !isEditMode) handleAddNew();
        else setViewMode(mode);
      }}
    >
      {viewMode === 'form' ? (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          <form onSubmit={handleSave} noValidate>
            <FormSection title="01. Partner Lab Identification" description="Core identifiers, legal partner code and regulatory accreditations">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Reference Lab Code <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.labCode ? 'border-danger' : ''}`}
                    placeholder="e.g. QDL-001, LAL-001"
                    value={formData.labCode}
                    onChange={(e) => handleChange('labCode', e.target.value.toUpperCase())}
                    disabled={isEditMode}
                  />
                  {errors.labCode && <span className="error-message">{errors.labCode}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Laboratory Name <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.labName ? 'border-danger' : ''}`}
                    placeholder="e.g. Quest Diagnostics National Reference Lab"
                    value={formData.labName}
                    onChange={(e) => handleChange('labName', e.target.value)}
                  />
                  {errors.labName && <span className="error-message">{errors.labName}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Accreditations & Certifications</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. CAP, NABL ISO 15189"
                    value={formData.accreditation}
                    onChange={(e) => handleChange('accreditation', e.target.value)}
                  />
                </div>
              </div>
            </FormSection>

            <FormSection title="02. Logistics & Commercial Agreement" description="Default expected turnaround time, contract validities and commercial billing terms">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Default TAT (Hours)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="24"
                    value={formData.defaultTatHours}
                    onChange={(e) => handleChange('defaultTatHours', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Agreement Start Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.agreementStartDate}
                    onChange={(e) => handleChange('agreementStartDate', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Agreement End Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.agreementEndDate}
                    onChange={(e) => handleChange('agreementEndDate', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Billing Terms</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Monthly B2B Net 30"
                    value={formData.billingTerms}
                    onChange={(e) => handleChange('billingTerms', e.target.value)}
                  />
                </div>
              </div>
            </FormSection>

            <FormSection title="03. Contact & Dispatch Address" description="Facility address for courier pickup/drop and operational escalation contacts">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Contact Person</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Dr. Arvind Mehra"
                    value={formData.contactPerson}
                    onChange={(e) => handleChange('contactPerson', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Contact Phone</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="+91 98765 43210"
                    value={formData.contactPhone}
                    onChange={(e) => handleChange('contactPhone', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Official Email</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="reference@labpartner.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </div>

                <div className="form-group col-span-2">
                  <label className="form-label">Dispatch / Facility Address</label>
                  <textarea
                    className="form-input"
                    rows={2}
                    placeholder="Complete street address, sector, city and pincode"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </div>
              </div>
            </FormSection>

            <div className="form-actions-bar" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewMode('list')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : isEditMode ? 'Update Reference Lab' : 'Create Reference Lab'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          {/* Controls Bar */}
          <div className="filter-bar">
            <div className="filter-bar-left">
              <div style={{ position: 'relative', width: '320px', minWidth: '240px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '2.4rem' }}
                  placeholder="Search by Lab Code or Name..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>

              <select
                className="form-select"
                style={{ width: '160px', minWidth: '140px' }}
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>

            <div className="filter-bar-right">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddNew}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>+ New Reference Lab</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive" style={{ marginTop: '1rem', overflowX: 'auto' }}>
            <table className="master-table mvc-table" style={{ width: '100%', minWidth: '1000px' }}>
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Code</th>
                  <th style={{ minWidth: '220px' }}>Laboratory Name</th>
                  <th style={{ width: '140px' }}>Accreditation</th>
                  <th style={{ width: '110px', textAlign: 'center' }}>Default TAT</th>
                  <th style={{ minWidth: '160px' }}>Billing Terms</th>
                  <th style={{ minWidth: '180px' }}>Contact</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Active Tests</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      Loading reference laboratories...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      No reference laboratories found matching your criteria.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((lab) => (
                    <tr key={lab.referenceLabId}>
                      <td>
                        <span className="badge badge-primary" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{lab.labCode}</span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{lab.labName}</div>
                        {lab.address && <div className="text-muted" style={{ fontSize: '11px', marginTop: '2px' }}>{lab.address}</div>}
                      </td>
                      <td>
                        <span className="badge badge-info" style={{ fontSize: '11px', padding: '2px 8px' }}>
                          {lab.accreditation || 'Standard'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>{lab.defaultTatHours} hrs</td>
                      <td>{lab.billingTerms || 'N/A'}</td>
                      <td>
                        {lab.contactPerson && <div style={{ fontWeight: 500 }}>{lab.contactPerson}</div>}
                        {lab.contactPhone && <div className="text-muted" style={{ fontSize: '11px' }}>{lab.contactPhone}</div>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="badge badge-secondary" style={{ fontWeight: 600 }}>{lab.activeTestCount || 0}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${lab.isActive ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: '11px', padding: '2px 8px' }}>
                          {lab.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(lab)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${lab.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
                            onClick={() => handleToggleStatus(lab)}
                          >
                            {lab.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Centralized Pagination */}
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[15, 25, 50, 100, 200]}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(confirmTarget)}
        title={`${confirmTarget?.action === 'activate' ? 'Activate' : 'Deactivate'} Reference Lab`}
        message={`Are you sure you want to ${confirmTarget?.action} "${confirmTarget?.name}"?`}
        confirmText={confirmTarget?.action === 'activate' ? 'Activate' : 'Deactivate'}
        confirmVariant={confirmTarget?.action === 'activate' ? 'success' : 'danger'}
        onConfirm={executeStatusChange}
        onCancel={() => setConfirmTarget(null)}
        loading={statusUpdating}
      />
    </MasterPageWrapper>
  );
};

export default ReferenceLabMasterPage;
